import { useTranslation } from 'react-i18next';

import { Input } from '@/components';

import { NumberInput } from '../controls/NumberInput';
import { StyleToggleButton } from '../controls/StyleToggleButton';
import { OVERFLOW_OPTIONS } from '../styleConstants';
import type { LayoutElementStyle } from '../styleTypes';

interface SizeSectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
}

export function SizeSection({ style, onChange }: SizeSectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';
  return (
    <div className="space-y-3">
      {/* Width */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}common.width`)}
        </label>
        <div className="flex gap-1">
          {(
            [
              { v: undefined, label: t(`${p}common.auto`) },
              { v: '100%', label: '100%' },
              { v: '50%', label: '50%' },
              { v: 'custom', label: t(`${p}size.widthCustom`) },
            ] as const
          ).map(({ v, label }) => {
            const isCustom = v === 'custom';
            const isActive = isCustom
              ? !!(
                  style.width &&
                  style.width !== '100%' &&
                  style.width !== '50%' &&
                  style.width !== 'auto'
                )
              : style.width === v || (!style.width && !v);
            return (
              <StyleToggleButton
                key={label}
                active={isActive}
                onClick={() => {
                  if (isCustom) {
                    onChange({ width: '300px' });
                  } else {
                    onChange({ width: v as string | undefined });
                  }
                }}
              >
                {label}
              </StyleToggleButton>
            );
          })}
        </div>
        {style.width &&
          style.width !== '100%' &&
          style.width !== '50%' &&
          style.width !== 'auto' && (
            <div className="mt-2">
              <Input
                type="text"
                value={style.width}
                onChange={(e) => onChange({ width: e.target.value || undefined })}
                className="px-2 py-1.5 font-mono text-xs"
                placeholder={t(`${p}size.customPlaceholder`)}
              />
            </div>
          )}
      </div>

      {/* Min / Max Height */}
      <div className="flex flex-wrap gap-2">
        <NumberInput
          value={style.minHeight}
          onChange={(v) => onChange({ minHeight: v })}
          min={0}
          max={2000}
          label={t(`${p}size.minHeight`)}
          unit="px"
          className="min-w-[7rem] flex-1"
        />
        <NumberInput
          value={style.maxHeight}
          onChange={(v) => onChange({ maxHeight: v })}
          min={0}
          max={2000}
          label={t(`${p}size.maxHeight`)}
          unit="px"
          className="min-w-[7rem] flex-1"
        />
      </div>

      {/* Overflow */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}size.overflow`)}
        </label>
        <div className="flex gap-1">
          {OVERFLOW_OPTIONS.map(({ value, label }) => (
            <StyleToggleButton
              key={value}
              active={(style.overflow || 'visible') === value}
              onClick={() => onChange({ overflow: value as LayoutElementStyle['overflow'] })}
              className="text-[10px]"
            >
              {label}
            </StyleToggleButton>
          ))}
        </div>
      </div>
    </div>
  );
}
